import { Refine } from '@refinedev/core';
import { RefineSnackbarProvider, useNotificationProvider } from '@refinedev/mui';
import routerProvider from '@refinedev/react-router';
import dataProvider from '@refinedev/simple-rest';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { CssBaseline, ThemeProvider } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import type { PaletteMode } from '@mui/material';
import { buildTheme, ColorModeContext, initialColorMode, persistColorMode } from './theme';
import { LanguageProvider } from './i18n';
import { LoginPage } from './pages/login';
import { OrganizationSetupPage } from './pages/organization-setup';
import { LandingPage } from './pages/landing';
import { DashboardPage } from './pages/dashboard';
import { AnnouncementsListPage } from './pages/announcements/list';
import { AnnouncementCreatePage } from './pages/announcements/create';
import { AnnouncementEditPage } from './pages/announcements/edit';
import { InvoicesListPage } from './pages/invoices/list';
import { InvoiceShowPage } from './pages/invoices/show';
import { InvoiceCreatePage } from './pages/invoices/create';
import { FacilitiesListPage } from './pages/facilities/list';
import { FacilityCreatePage } from './pages/facilities/create';
import { FacilityEditPage } from './pages/facilities/edit';
import { BookingsListPage } from './pages/bookings/list';
import { ComplaintsListPage } from './pages/complaints/list';
import { ComplaintShowPage } from './pages/complaints/show';
import { PaymentRemindersPage } from './pages/reminders/list';
import { UnitsListPage } from './pages/units/list';
import { ResidentsListPage } from './pages/residents/list';
import { OperationsPage } from './pages/operations/list';
import { ReportsPage } from './pages/reports/list';
import { FeesPage } from './pages/billing/fees';
import { PlatformDashboardPage } from './pages/platform/dashboard';
import { PlatformDevelopersPage } from './pages/platform/developers';
import { PlatformClustersPage } from './pages/platform/clusters';
import { PlatformArchivedPage } from './pages/platform/archived';
import { DeveloperManagersPage } from './pages/users/developer-managers';
import { ClusterAdminsPage } from './pages/users/cluster-admins';
import { SecurityPage } from './pages/users/security';
import { ResidentsPage } from './pages/users/residents';
import { ResidentAppBrandingPage } from './pages/resident-app/branding';
import { ProtectedLayout } from './layout';
import { authProvider } from './auth-provider';
import { api } from './lib/api-client';
import { ClerkAuthBridge } from './lib/clerk-auth';

const API_URL = (import.meta.env.VITE_API_URL ?? 'http://localhost:8787/api/v1').replace(/\/$/, '');

function App() {
  // Warm Stone theme with a persisted light/dark mode (see ./theme).
  const [mode, setMode] = useState<PaletteMode>(initialColorMode);
  const theme = useMemo(() => buildTheme(mode), [mode]);
  const colorMode = useMemo(
    () => ({
      mode,
      toggle: () =>
        setMode((prev) => {
          const next: PaletteMode = prev === 'light' ? 'dark' : 'light';
          persistColorMode(next);
          return next;
        }),
    }),
    [mode],
  );

  // Bridge api-client's "logout" event (fired when refresh fails) to a hard redirect.
  useEffect(() => {
    const onLogout = () => {
      if (window.location.pathname !== '/login') window.location.href = '/login';
    };
    window.addEventListener('livinhome:auth:logout', onLogout);
    return () => window.removeEventListener('livinhome:auth:logout', onLogout);
  }, []);

  return (
    <LanguageProvider>
    <ColorModeContext.Provider value={colorMode}>
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <RefineSnackbarProvider>
        <BrowserRouter>
          <ClerkAuthBridge />
          <Refine
            dataProvider={dataProvider(API_URL, api)}
            authProvider={authProvider}
            routerProvider={routerProvider}
            notificationProvider={useNotificationProvider}
            options={{
              syncWithLocation: true,
              warnWhenUnsavedChanges: true,
              useNewQueryKeys: true,
              projectId: 'livinhome-admin',
            }}
            // Resources are registered in subsequent phases as modules land.
            resources={[]}
          >
            <Routes>
              <Route path="/" element={<LandingPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/onboarding" element={<OrganizationSetupPage />} />
              <Route element={<ProtectedLayout />}>
                <Route path="/dashboard" element={<DashboardPage />} />
                <Route path="/announcements" element={<AnnouncementsListPage />} />
                <Route path="/announcements/new" element={<AnnouncementCreatePage />} />
                <Route path="/announcements/:id/edit" element={<AnnouncementEditPage />} />
                <Route path="/invoices" element={<InvoicesListPage />} />
                <Route path="/invoices/new" element={<InvoiceCreatePage />} />
                <Route path="/invoices/:id" element={<InvoiceShowPage />} />
                <Route path="/facilities" element={<FacilitiesListPage />} />
                <Route path="/facilities/new" element={<FacilityCreatePage />} />
                <Route path="/facilities/:id/edit" element={<FacilityEditPage />} />
                <Route path="/bookings" element={<BookingsListPage />} />
                <Route path="/complaints" element={<ComplaintsListPage />} />
                <Route path="/complaints/:id" element={<ComplaintShowPage />} />
                <Route path="/reminders" element={<PaymentRemindersPage />} />
                <Route path="/units" element={<UnitsListPage />} />
                <Route path="/residents" element={<ResidentsListPage />} />
                <Route path="/operations" element={<OperationsPage />} />
                <Route path="/reports" element={<ReportsPage />} />
                <Route path="/billing/fees" element={<FeesPage />} />
                <Route path="/platform" element={<Navigate to="/platform/developers" replace />} />
                <Route path="/platform/dashboard" element={<PlatformDashboardPage />} />
                <Route path="/platform/developers" element={<PlatformDevelopersPage />} />
                <Route path="/platform/clusters" element={<PlatformClustersPage />} />
                <Route path="/platform/archived" element={<PlatformArchivedPage />} />
                <Route path="/users/developer-managers" element={<DeveloperManagersPage />} />
                <Route path="/users/cluster-admins" element={<ClusterAdminsPage />} />
                <Route path="/users/security" element={<SecurityPage />} />
                <Route path="/users/residents" element={<ResidentsPage />} />
                <Route path="/resident-app/branding" element={<ResidentAppBrandingPage />} />
              </Route>
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Refine>
        </BrowserRouter>
      </RefineSnackbarProvider>
    </ThemeProvider>
    </ColorModeContext.Provider>
    </LanguageProvider>
  );
}

export default App;
